import { cn } from './cn'

export interface FunnelStage {
  stage: string
  count: number
}

export interface FunnelProps {
  data?: FunnelStage[]
  ariaLabel?: string
  empty?: boolean
  emptyLabel?: string
  loading?: boolean
  className?: string
}

/**
 * Enrolled -> active -> completed funnel as stacked Meter-style bars (each bar
 * widthed against the first/largest stage). House-built on Tailwind tokens; no
 * chart dependency. Honest empty card for `data === []`.
 */
export function Funnel({
  data = [],
  ariaLabel,
  empty,
  emptyLabel = 'No cohort progress yet',
  loading = false,
  className = ''
}: FunnelProps) {
  const isEmpty = empty ?? data.length === 0
  if (loading) {
    return <EmptyFunnel className={className} label="Loading…" role="status" />
  }
  if (isEmpty) {
    return <EmptyFunnel className={className} label={emptyLabel} />
  }

  const ceiling = Math.max(1, ...data.map((stage) => stage.count))
  const summary = ariaLabel ?? `Funnel: ${data.map((stage) => `${stage.stage} ${stage.count}`).join(' to ')}.`

  return (
    <figure className={cn('m-0 flex flex-col gap-3', className)} role="img" aria-label={summary}>
      {data.map((stage, index) => {
        const widthPct = Math.round((stage.count / ceiling) * 100)
        return (
          <div key={stage.stage} className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-xs text-ink-muted">
              <span>{stage.stage}</span>
              <span className="tabular-nums font-medium text-strong">{stage.count}</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-pill bg-stone">
              <span
                className={cn('block h-full rounded-pill', index === data.length - 1 ? 'bg-ok' : 'bg-chart-1')}
                style={{ inlineSize: `${Math.max(2, widthPct)}%` }}
              />
            </div>
          </div>
        )
      })}
      <figcaption className="sr-only">{summary}</figcaption>
    </figure>
  )
}

function EmptyFunnel({ className = '', label, role }: { className?: string; label: string; role?: string }) {
  return (
    <div
      className={cn('grid h-24 w-full place-items-center rounded-control border border-dashed border-line bg-whisper', className)}
      role={role}
    >
      <span className="px-4 text-center text-xs leading-5 text-ink-muted">{label}</span>
    </div>
  )
}
